import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import PageWrapper from '../layout/PageWrapper';

export default function Calendar() {
  const [currentPage, setCurrentPage] = useState(1);

  // Month to page mapping for the 2025-2026 school year calendar
  // Page 1 = Cover, Page 2 = September 2025, Last page = August 2026
  const monthToPage: { [key: string]: number } = {
    'September 2025': 2,
    'October 2025': 3,
    'November 2025': 4,
    'December 2025': 5,
    'January 2026': 6,
    'February 2026': 7,
    'March 2026': 8,
    'April 2026': 9,
    'May 2026': 10,
    'June 2026': 11,
    'July 2026': 12,
    'August 2026': 13
  };

  const getCurrentMonthPage = () => {
    const now = new Date();
    const currentMonth = now.toLocaleString('default', { month: 'long' });
    const currentYear = now.getFullYear();
    const monthKey = `${currentMonth} ${currentYear}`;
    
    // If current date is within the school year (Sep 2025 - Aug 2026), show that month
    // Otherwise default to September 2025 (start of school year) or cover page
    return monthToPage[monthKey] || 2; // Default to September 2025 instead of cover
  };

  useEffect(() => {
    setCurrentPage(getCurrentMonthPage());
  }, []);

  const pdfUrl = `/documents/[KEY]%202025-2026%20CALENDAR%20(1).pdf#page=${currentPage}`;

  const months = [
    'September 2025', 'October 2025', 'November 2025', 'December 2025',
    'January 2026', 'February 2026', 'March 2026', 'April 2026',
    'May 2026', 'June 2026', 'July 2026', 'August 2026'
  ];

  // Also include cover page option
  const allOptions = [
    { label: 'Cover Page', value: 'cover', page: 1 },
    ...months.map(month => ({ label: month, value: month, page: monthToPage[month] }))
  ];
  return (
    <PageWrapper>
      <motion.div
        className="max-w-6xl mx-auto"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-dblue mb-2">
              📅 School Calendar
            </h1>
            <p className="text-gray-600">
              2025-2026 Academic Year
            </p>
          </motion.div>
        </div>

        {/* Calendar Container */}
        <motion.div
          className="bg-white rounded-2xl shadow-lg overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-dblue to-blue-600">
            <div className="flex items-center justify-between">
              <div className="text-white">
                <h2 className="text-xl font-semibold">
                  Harbourside Children's Centre Calendar
                </h2>
                <p className="text-blue-100 text-sm mt-1">
                  Stay up to date with important dates and school events
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Month Navigation */}
                <select
                  value={allOptions.find(option => option.page === currentPage)?.value || 'cover'}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    if (selectedValue === 'cover') {
                      setCurrentPage(1);
                    } else {
                      const pageNumber = monthToPage[selectedValue] || 2;
                      setCurrentPage(pageNumber);
                    }
                  }}
                  className="px-3 py-2 bg-white text-dblue rounded-lg border border-gray-200 text-sm font-medium"
                >
                  {allOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => window.open(pdfUrl, '_blank')}
                    className="px-4 py-2 bg-white text-dblue rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Open in New Tab
                  </button>
                  <a
                    href="/documents/2025-2026 CALENDAR.pdf"
                    download="HCC-Calendar-2025-2026.pdf"
                    className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium"
                  >
                    Download PDF
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="p-6">
            <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '80vh' }}>
              <iframe
                key={currentPage}
                src={pdfUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="School Calendar 2025-2026"
              >
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-8">
                    <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-600 mb-4">
                      Your browser doesn't support PDF viewing.
                    </p>
                    <a
                      href="/documents/2025-2026 CALENDAR.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-dblue text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download Calendar PDF
                    </a>
                  </div>
                </div>
              </iframe>
            </div>
          </div>

          {/* Additional Info */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="font-medium text-dblue mb-2">📅 Current View</h3>
                <p className="text-sm text-gray-600">
                  {allOptions.find(option => option.page === currentPage)?.label || 'Cover Page'}
                </p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="font-medium text-dblue mb-2">📚 School Hours</h3>
                <p className="text-sm text-gray-600">Monday - Friday<br />7:00 AM - 6:00 PM</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="font-medium text-dblue mb-2">📞 Contact Info</h3>
                <p className="text-sm text-gray-600">Phone: (604) 986-2388<br />For calendar questions</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm">
                <h3 className="font-medium text-dblue mb-2">🔄 Updates</h3>
                <p className="text-sm text-gray-600">Calendar is updated regularly<br />Check back for changes</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </PageWrapper>
  );
}
